import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';

import usePage from '@dashboard/hooks/usePage';

import PaymentsBookingsList from '@dashboard/scenes/PageOverview/components/PaymentsBookingsList';

import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import useUrlQuery from '@/dashboard/hooks/useUrlQuery';
import { stringify } from 'query-string';
import { useHistory } from 'react-router';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  EMAIL_ASCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Email A-Z',
  },
  EMAIL_DESCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Email Z-A',
  },
};

const FILTER_OPTIONS = {
  ALL: {
    value: 'all',
    label: 'All',
  },
  PAID: {
    value: 'paid',
    label: 'Paid',
  },
  PARTIALLY_PAID: {
    value: 'partially_paid',
    label: 'Partially paid',
  },
  RECOVERED: {
    value: 'recovered',
    label: 'Recovered',
  },
  UNPAID: {
    value: 'unpaid',
    label: 'Unpaid',
  },
  INCOMPLETE: {
    value: 'incomplete',
    label: 'Incomplete',
  },
  FAILED: {
    value: 'failed',
    label: 'Failed',
  },
  REFUNDED: {
    value: 'refunded',
    label: 'Refunded',
  },
  PARTIAL_REFUND: {
    value: 'partial_refund',
    label: 'Partial refund',
  },
  CANCELED: {
    value: 'canceled',
    label: 'Canceled',
  },
  ABANDONED: {
    value: 'abandoned',
    label: 'Abandoned',
  },
};

function getSortOptionFromQuery(sortBy, sortDirection) {
  const sortOption = Object.keys(SORT_OPTIONS).find(
    key => SORT_OPTIONS[key].value === sortBy && SORT_OPTIONS[key].direction === sortDirection,
  );

  return sortOption || Object.keys(SORT_OPTIONS)[0];
}

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );

  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

const PageBookings = () => {
  const { page } = usePage();
  const history = useHistory();

  const query = useUrlQuery();
  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));
  const [ticketGroupFilter, setTicketGroupFilter] = useState(query.ticketGroup || 'ALL');
  const [ticketTypeFilter, setTicketTypeFilter] = useState(query.ticketType || 'ALL');

  const ticketGroupOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All ticket groups',
      },
      ...(page.ticketGroups?.reduce((acc, group) => {
        acc[group._id] = {
          value: group._id,
          label: group.name,
        };
        return acc;
      }, {}) || {}),
    }),
    [page],
  );

  const ticketTypeOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All tickets',
      },
      ...(page.ticketGroups?.reduce((acc, group) => {
        if (
          ticketGroupFilter !== 'ALL' &&
          group._id !== ticketGroupOptions[ticketGroupFilter]?.value
        ) {
          return acc;
        }

        group.ticketTypes?.forEach(ticketType => {
          acc[ticketType._id] = {
            value: ticketType._id,
            label: `${ticketType.name} (${group.name})`,
          };
        });
        return acc;
      }, {}) || {}),
    }),
    [page, ticketGroupFilter, ticketGroupOptions],
  );

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  useEffect(() => {
    setTicketGroupFilter(query.ticketGroup || 'ALL');
  }, [query.ticketGroup]);

  useEffect(() => {
    setTicketTypeFilter(query.ticketType || 'ALL');
  }, [query.ticketType]);

  useEffect(() => {
    if (ticketTypeFilter !== 'ALL' && !ticketTypeOptions[ticketTypeFilter]) {
      setTicketTypeFilter('ALL');
    }
  }, [ticketGroupFilter, ticketTypeFilter, ticketTypeOptions]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
  };

  const ticketFilters = {
    ...(ticketGroupOptions[ticketGroupFilter]?.value && {
      ['tickets.ticketGroup']: ticketGroupOptions[ticketGroupFilter].value,
    }),
    ...(ticketTypeOptions[ticketTypeFilter]?.value && {
      ['tickets.ticketType']: ticketTypeOptions[ticketTypeFilter].value,
    }),
  };

  const filter = {
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    ...ticketFilters,
    page: page._id,
    livemode: true,
    search,
  };

  useEffect(() => {
    const params = {};

    params.sortBy = sortBy;
    params.sortDirection = sortDirection;

    if (search) {
      params.search = search;
    }

    if (FILTER_OPTIONS[status].value !== 'all') {
      params.status = FILTER_OPTIONS[status].value;
    }

    if (ticketGroupFilter !== 'ALL' && ticketGroupOptions[ticketGroupFilter]?.value) {
      params.ticketGroup = ticketGroupFilter;
    }

    if (ticketTypeFilter !== 'ALL' && ticketTypeOptions[ticketTypeFilter]?.value) {
      params.ticketType = ticketTypeFilter;
    }

    history.replace({
      search: stringify(params),
    });
  }, [
    sortBy,
    sortDirection,
    status,
    search,
    ticketGroupFilter,
    ticketTypeFilter,
    ticketGroupOptions,
    ticketTypeOptions,
  ]);

  return (
    <div className="mb-24 overflow-auto">
      <div className="flex items-center justify-between pb-4">
        <h2 className="text-xl font-medium">Bookings</h2>

        <div className="flex items-center gap-x-2">
          <SearchInput
            onChange={setSearch}
            name="search_customers"
            placeholder="Search by email or code"
            initialValue={search}
          />

          <FilterInput
            options={ticketGroupOptions}
            onChange={setTicketGroupFilter}
            value={ticketGroupFilter}
          />

          <FilterInput
            options={ticketTypeOptions}
            onChange={setTicketTypeFilter}
            value={ticketTypeFilter}
          />

          <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />

          <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

          <ExportView
            collection="payments"
            label="payments"
            filters={{
              ...filter,
              page: page._id,
              sort: JSON.stringify(sort),
            }}
          />
        </div>
      </div>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header" className="!bg-gray-50 !mt-0 rounded-t">
          <CardListItemElement size="fill" align="right">
            Guest
          </CardListItemElement>
          <CardListItemElement size="xl">Booking ID</CardListItemElement>
          <CardListItemElement size="xl">Amount</CardListItemElement>
          <CardListItemElement size="large">Tickets</CardListItemElement>
          <CardListItemElement size="xl">Booked on</CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <PaymentsBookingsList
          sort={sort}
          search={search}
          filter={filter}
          options={{
            hidePaymentLink: true,
            hidePageType: true,
          }}
        />
      </CardContent>
    </div>
  );
};

PageBookings.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default PageBookings;
